'use client'

import { useId } from 'react'

/** Soft, distinct palette per world — clean flat marks, no neon glow. */
const PALETTE = {
  hub: { a: '#6ec8ff', b: '#3a8fd4', c: '#d4ecff', ink: '#0a1218' },
  equity: { a: '#6ee7c8', b: '#2a9d7a', c: '#d1faf0', ink: '#0a1410' },
  nightsky: { a: '#9dabff', b: '#5b6fd4', c: '#e4e8ff', ink: '#0c0e18' },
  roomie: { a: '#f0a060', b: '#c46830', c: '#ffe8d4', ink: '#161008' },
  grey: { a: '#bfb09a', b: '#8a7860', c: '#ede6dc', ink: '#12100c' },
  nothooked: { a: '#d3b8fe', b: '#7c3aed', c: '#f1eaf9', ink: '#120b1c' },
  brainpower: { a: '#22d3ee', b: '#2563eb', c: '#d7f7ff', ink: '#071018' },
  ritmorbit: { a: '#f080b0', b: '#b84078', c: '#fce0ee', ink: '#140810' },
  rondoiq: { a: '#34d399', b: '#f5c16c', c: '#e7fff4', ink: '#07120d' },
  scout: { a: '#f0c050', b: '#a87818', c: '#fef4d8', ink: '#141008' },
  gamedev: { a: '#b898f8', b: '#7858c8', c: '#ede4ff', ink: '#0e0814' },
}

const S = 1.05

function MarkSvg({ palette, className, children, size = 40 }) {
  return (
    <svg
      className={['app-mark', className].filter(Boolean).join(' ')}
      viewBox="0 0 40 40"
      width={size}
      height={size}
      fill="none"
      aria-hidden
    >
      <rect x="2" y="2" width="36" height="36" rx="10" fill={palette.ink} />
      <rect x="2" y="2" width="36" height="36" rx="10" fill={palette.a} fillOpacity="0.07" />
      <rect x="2" y="2" width="36" height="36" rx="10" stroke={palette.a} strokeWidth="1" strokeOpacity="0.32" />
      {children}
    </svg>
  )
}

function p(id) {
  return PALETTE[id] || PALETTE.hub
}

/** Official UC monogram, from unity-cosmos-brand/logo-mark.svg. */
const UC_TUBES = [
  'M -158,-100 L -158,20 A 80,80 0 0,0 2,20 L 2,0 A 68,68 0 0,1 131.6,-28.7',
  'M 2,0 A 68,68 0 0,0 131.6,28.7',
  'M -142,-100 L -142,20 A 64,64 0 0,0 -14,20 L -14,0 A 84,84 0 0,1 146.1,-35.5',
  'M -14,0 A 84,84 0 0,0 146.1,35.5',
  'M -126,-100 L -126,20 A 48,48 0 0,0 -30,20 L -30,0 A 100,100 0 0,1 160.6,-42.3',
  'M -30,0 A 100,100 0 0,0 160.6,42.3',
]

const UC_LAYERS = [
  ['#0088ff', 30, 0.12],
  ['#00c0ff', 18, 0.28],
  ['#00e5ff', 9, 0.75],
  ['#90f2ff', 4.5, 0.95],
  ['#ffffff', 1.8, 1],
]

/** Hub — official neon UC. Shared by every world's top bar. */
export function HubMark({ className, size = 40 }) {
  const glow = `uc-mark-${useId().replace(/:/g, '')}`
  const height = size
  const width = Math.round(size * (400 / 280))
  return (
    <svg
      className={['app-mark', 'app-mark--hub', className].filter(Boolean).join(' ')}
      viewBox="-200 -140 400 280"
      width={width}
      height={height}
      fill="none"
      aria-hidden
    >
      <defs>
        <filter id={glow} x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="6" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
      <g filter={`url(#${glow})`} fill="none" strokeLinecap="round" strokeLinejoin="round">
        {UC_LAYERS.map(([stroke, strokeWidth, opacity]) => (
          <g key={stroke} stroke={stroke} strokeWidth={strokeWidth} opacity={opacity}>
            {UC_TUBES.map((d) => (
              <path key={d} d={d} />
            ))}
          </g>
        ))}
      </g>
    </svg>
  )
}

/** Equity Cosmos — dramatic financial rise & fall (red bear plunge ↘ & green bull surge ↗) forming an abstract market smile. */
export function EquityMark({ className, size }) {
  const c = p('equity')
  return (
    <MarkSvg palette={c} className={className} size={size}>
      <g transform="rotate(-4 20 20)">
        {/* Subtle Candlestick Wicks / Gestalt facial balance */}
        <line x1="9" y1="10.5" x2="9" y2="13.5" stroke="#f43f5e" strokeWidth={S * 0.55} strokeLinecap="round" opacity={0.65} />
        <line x1="31" y1="7" x2="31" y2="10" stroke={c.a} strokeWidth={S * 0.55} strokeLinecap="round" opacity={0.75} />

        {/* Red Bear Plunge (Bajada pronunciada) */}
        <path
          d="M 9 13.5 L 13 22 L 15.5 19 L 19.5 28.5"
          stroke="#f43f5e"
          strokeWidth={S * 1.15}
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
        <path
          d="M 16 28.5 L 19.5 28.5 L 19.5 25"
          stroke="#f43f5e"
          strokeWidth={S * 0.95}
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />

        {/* Green Bull Surge (Subida pronunciada) */}
        <path
          d="M 20.5 28.5 L 24.5 19.5 L 27 22.5 L 31 10"
          stroke={c.a}
          strokeWidth={S * 1.15}
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
        <path
          d="M 27 10 H 31 V 14"
          stroke={c.a}
          strokeWidth={S * 0.95}
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />

        {/* Abstract Market Support Arc (Subtle smile contour) */}
        <path
          d="M 11 29.5 Q 20 34 29 29.5"
          stroke={c.b}
          strokeWidth={S * 0.6}
          strokeLinecap="round"
          strokeOpacity={0.4}
          fill="none"
        />
      </g>
    </MarkSvg>
  )
}

/** Night Sky Diary — celestial crescent moon, nebula waves, and cosmic constellation stars. */
export function NightSkyMark({ className, size }) {
  const c = p('nightsky')
  return (
    <MarkSvg palette={c} className={className} size={size}>
      {/* Luminous Crescent Moon */}
      <path
        d="M 13 12 A 8.5 8.5 0 1 0 23 27 A 8 8 0 0 1 13 12 Z"
        stroke={c.a}
        strokeWidth={S * 1.05}
        fill={c.a}
        fillOpacity="0.18"
        strokeLinejoin="round"
      />
      {/* Radiant Star Diamond */}
      <path
        d="M 27 10 L 28 13 L 31 14 L 28 15 L 27 18 L 26 15 L 23 14 L 26 13 Z"
        fill={c.c}
      />
      {/* Constellation Sparkles */}
      <circle cx="28" cy="22" r="1.1" fill={c.a} />
      <circle cx="23" cy="25" r="0.75" fill={c.c} />
      <circle cx="17" cy="8" r="0.85" fill={c.a} fillOpacity="0.8" />
      {/* Dream Horizon Arc */}
      <path
        d="M 10 29.5 Q 20 25.5 30 29.5"
        stroke={c.b}
        strokeWidth={S * 0.75}
        strokeLinecap="round"
        strokeOpacity="0.55"
        fill="none"
      />
    </MarkSvg>
  )
}

/** Roomie — shared home + two roommates. */
export function RoomieMark({ className, size }) {
  const c = p('roomie')
  return (
    <MarkSvg palette={c} className={className} size={size}>
      <path d="M13 16 L20 10.5 L27 16" stroke={c.a} strokeWidth={S} strokeLinejoin="round" />
      <path d="M14.5 16 V20.5 H25.5 V16" stroke={c.b} strokeWidth={S * 0.7} strokeLinejoin="round" strokeOpacity="0.7" />
      <circle cx="12.5" cy="26" r="2" stroke={c.c} strokeWidth={S * 0.8} />
      <path d="M12.5 28 V30.5 M10.5 30 H14.5" stroke={c.c} strokeWidth={S * 0.65} strokeLinecap="round" />
      <circle cx="27.5" cy="26" r="2" stroke={c.c} strokeWidth={S * 0.8} />
      <path d="M27.5 28 V30.5 M25.5 30 H29.5" stroke={c.c} strokeWidth={S * 0.65} strokeLinecap="round" />
      <path d="M16 27.5 Q20 26 24 27.5" stroke={c.a} strokeWidth={S * 0.65} strokeLinecap="round" fill="none" />
    </MarkSvg>
  )
}

/** Grey Senior — one mask. Her hair on the left, his mustache on the right. */
export function GreyMark({ className, size }) {
  const c = p('grey')
  return (
    <MarkSvg palette={c} className={className} size={size}>
      <path
        d="M20 10.6 C14.2 10.6 11 15 11 20.4 C11 26.2 14.4 30.4 20 30.4 C25.6 30.4 29 26.2 29 20.4 C29 15 25.8 10.6 20 10.6"
        stroke={c.a}
        strokeWidth={S * 1.15}
        fill={c.a}
        fillOpacity="0.16"
      />
      <path d="M20 12.2 V28.6" stroke={c.b} strokeWidth={S * 0.75} strokeLinecap="round" strokeOpacity="0.75" />
      <path
        d="M17.2 12.4 C13 13 10.4 16.8 10.4 21.2 C10.4 26.6 12.8 31 16.2 32.6"
        stroke={c.c}
        strokeWidth={S * 1.25}
        strokeLinecap="round"
      />
      <path d="M21.6 12 C24.8 11.6 27.4 13.6 28.2 16.4" stroke={c.a} strokeWidth={S * 0.9} strokeLinecap="round" />
      <path d="M21.2 22.4 Q24 24.4 26.8 22" stroke={c.b} strokeWidth={S * 1.25} strokeLinecap="round" />
    </MarkSvg>
  )
}

/** Not Hooked — one green leaf, same weight as the other world marks. */
export function NotHookedMark({ className, size }) {
  const c = p('nothooked')
  return (
    <MarkSvg palette={c} className={className} size={size}>
      <path d="M10 18a10 10 0 0 1 20 0" stroke={c.b} strokeWidth="1" strokeLinecap="round" opacity=".65" />
      <circle cx="20" cy="15" r="2.6" fill={c.c} />
      <path d="M16.5 22c.7-3 1.9-4.3 3.5-4.3s2.8 1.3 3.5 4.3M16.5 22l-4 2.5c-1.6 1-.8 3 1.3 3H20m3.5-5.5 4 2.5c1.6 1 .8 3-1.3 3H20" stroke={c.a} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M12 29c2.5 3 13.5 3 16 0M20 27.5v3" stroke={c.b} strokeWidth="1" strokeLinecap="round" />
    </MarkSvg>
  )
}

/** Cosmos Brain Power — minimal brain outline; kumiko joint hints in a few strokes. */
export function BrainPowerMark({ className, size }) {
  const c = p('brainpower')
  const w = S * 0.98
  return (
    <MarkSvg palette={c} className={className} size={size}>
      <path
        d="M20 10.2c-4.5 0.4-7.1 3.1-7.4 6.6-0.2 2.3 0.5 4.6 2.2 5.9 1.1 0.85 2.6 1.25 5.2 1.25s4.1-0.4 5.2-1.25c1.7-1.3 2.4-3.6 2.2-5.9-0.3-3.5-2.9-6.2-7.4-6.6z"
        stroke={c.a}
        strokeWidth={w}
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M20 11v13.5" stroke={c.b} strokeWidth={w * 0.5} strokeLinecap="round" opacity="0.55" />
      <path
        d="M13.2 14.8h1.9 M24.9 14.8h-1.9 M14.2 19.8l1.6 0.9 M25.8 19.8l-1.6 0.9"
        stroke={c.c}
        strokeWidth={w * 0.45}
        strokeLinecap="round"
        opacity="0.75"
      />
      <path
        d="M15.6 13.2l1.2 1.6 M24.4 13.2l-1.2 1.6"
        stroke={c.a}
        strokeWidth={w * 0.4}
        strokeLinecap="round"
        opacity="0.45"
      />
    </MarkSvg>
  )
}

/** Ritmorbit — a record, the voice across it, and one freestyle spark. */
export function FreestyleMark({ className, size }) {
  const c = p('ritmorbit')
  return (
    <MarkSvg palette={c} className={className} size={size}>
      <circle cx="18" cy="20.5" r="8.2" stroke={c.a} strokeWidth={S * 1.1} fill={c.a} fillOpacity="0.16" />
      <circle cx="18" cy="20.5" r="1.45" fill={c.c} />
      <path d="M11.4 21 Q14.6 15.6 18 21 T24.6 19.4" stroke={c.c} strokeWidth={S * 1.05} strokeLinecap="round" />
      <path d="M28.2 11 L29 13.2 L31.4 14 L29 14.8 L28.2 17 L27.4 14.8 L25 14 L27.4 13.2 Z" fill={c.c} />
    </MarkSvg>
  )
}

/** RondoIQ — a circle of players and the ball in the middle. */
export function RondoMark({ className, size }) {
  const c = p('rondoiq')
  return (
    <MarkSvg palette={c} className={className} size={size}>
      <circle cx="20" cy="20" r="8.6" stroke={c.a} strokeWidth={S * 1.1} fill={c.a} fillOpacity="0.14" />
      <circle cx="20" cy="12.2" r="1.35" fill={c.c} />
      <circle cx="27.8" cy="20" r="1.35" fill={c.c} />
      <circle cx="20" cy="27.8" r="1.35" fill={c.a} />
      <circle cx="12.2" cy="20" r="1.35" fill={c.a} />
      <circle cx="20" cy="20" r="2.1" stroke={c.b} strokeWidth={S * 0.9} fill={c.b} fillOpacity="0.35" />
    </MarkSvg>
  )
}

/** Football Scout — binoculars + ball. */
export function ScoutMark({ className, size }) {
  const c = p('scout')
  return (
    <MarkSvg palette={c} className={className} size={size}>
      <rect x="10" y="13" width="8" height="6" rx="3" stroke={c.a} strokeWidth={S * 0.85} />
      <rect x="22" y="13" width="8" height="6" rx="3" stroke={c.a} strokeWidth={S * 0.85} />
      <path d="M18 16 H22" stroke={c.b} strokeWidth={S * 0.65} strokeLinecap="round" strokeOpacity="0.6" />
      <circle cx="20" cy="26.5" r="4.2" stroke={c.a} strokeWidth={S * 0.85} />
      <path d="M20 23.5 V29.5 M17 26.5 H23" stroke={c.c} strokeWidth={S * 0.45} strokeLinecap="round" strokeOpacity="0.55" />
    </MarkSvg>
  )
}

/** Game Dev — code brackets + controller. */
export function GameDevMark({ className, size }) {
  const c = p('gamedev')
  return (
    <MarkSvg palette={c} className={className} size={size}>
      <path d="M10 14 V26 M10 14 Q13.5 20 10 26" stroke={c.a} strokeWidth={S * 0.9} strokeLinecap="round" strokeLinejoin="round" />
      <path d="M14 14 V26 M14 14 Q10.5 20 14 26" stroke={c.b} strokeWidth={S * 0.7} strokeLinecap="round" strokeLinejoin="round" strokeOpacity="0.65" />
      <rect x="19" y="16" width="12" height="9" rx="3.5" stroke={c.a} strokeWidth={S * 0.85} />
      <path d="M22 20 H24 M23 19 V21" stroke={c.c} strokeWidth={S * 0.55} strokeLinecap="round" />
      <circle cx="27" cy="19.5" r="0.9" fill={c.c} fillOpacity="0.85" />
      <circle cx="28.8" cy="21.2" r="0.9" stroke={c.b} strokeWidth={S * 0.45} />
    </MarkSvg>
  )
}

const MARKS = {
  hub: HubMark,
  equity: EquityMark,
  nightsky: NightSkyMark,
  roomie: RoomieMark,
  grey: GreyMark,
  nothooked: NotHookedMark,
  brainpower: BrainPowerMark,
  ritmorbit: FreestyleMark,
  rondoiq: RondoMark,
  scout: ScoutMark,
  gamedev: GameDevMark,
}

/** Render a world mark — palette is fixed per app; accent prop kept for API compat. */
export function AppMark({ id, accent: _accent, className, size }) {
  const Mark = MARKS[id]
  if (!Mark) return null
  return <Mark className={className} size={size} />
}
